/**
 * IdentityConsentModal — plain-language consent capture shown the first time
 * a patient (or a clinician on the patient's behalf) uploads a Clinical
 * Identification Photo.
 *
 * The wording is deliberately non-legal: patients read this on a phone,
 * often in a second language, sometimes under time pressure. Three
 * sentences that state purpose, scope, and non-disclosure — signed off
 * by tapping "I agree".
 *
 * Not a substitute for the formal privacy policy — the ConsentRecord row
 * written server-side captures who, when, and which version. Update the
 * version string in the parent when the wording changes.
 */
export default function IdentityConsentModal({
  onAgree,
  onDecline,
  isSubmitting = false,
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="identity-consent-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-5 border-b border-slate-100">
          <h2 id="identity-consent-title" className="text-lg font-semibold text-slate-900">
            Why we ask for a photo
          </h2>
        </div>

        <div className="p-5 space-y-3 text-sm text-slate-700 leading-relaxed">
          <p>
            Your clinical identification photo helps healthcare workers confirm
            they are treating the correct patient during consultations,
            investigations, dispensing, and emergencies.
          </p>
          <p>
            It is used only for healthcare delivery on this platform. It is{' '}
            <strong>not shared with employers or other organisations</strong>{' '}
            without your permission.
          </p>
          <p>
            You can update or remove this photo at any time in your settings.
          </p>
        </div>

        <div className="p-4 flex justify-end gap-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onDecline}
            disabled={isSubmitting}
            className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={onAgree}
            disabled={isSubmitting}
            className="text-sm px-4 py-1.5 rounded bg-wantok-700 hover:bg-wantok-800 disabled:opacity-60 text-white font-medium"
          >
            {isSubmitting ? 'Saving…' : 'I agree'}
          </button>
        </div>
      </div>
    </div>
  )
}
